import React from "react";
import { MdKeyboardArrowUp, MdChevronLeft, MdChevronRight } from "react-icons/md";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CourtGridTimeSlot, CourtGridCourt } from "../../../../models/CourtGridData";
import { GridHeader } from "../../../shared/GridHeader";
import { TimeSlot, TimeSlotData, SlotStatus } from "../../../shared/TimeSlot";
import EAIconButton from "../../../shared/Buttons/EAIconButton";
import useCourtGridTable from "./useCourtGridTable";
import { SaleStep } from "../../../../store/stepSaleConfig/stepSaleConfigReducer";
import "./style.scss";

interface IProps {
  displayedCourts: CourtGridCourt[];
  visibleHours: string[];
  hasPastHours: boolean;
  findSlot: (courtName: string, startTime: string) => CourtGridTimeSlot | undefined;
  getSlotStatus: (timeSlot: CourtGridTimeSlot) => SlotStatus;
  onSlotClick: (timeSlot: CourtGridTimeSlot, courtName: string) => void;
  onShowPastHours: () => void;
  // Mobile navigation props
  onPreviousCourt?: () => void;
  onNextCourt?: () => void;
  currentCourtIndex?: number;
  filteredCourtsLength?: number;
  saleStep?: SaleStep;
}

const CourtGridTable: React.FC<IProps> = ({
  displayedCourts,
  visibleHours,
  hasPastHours,
  findSlot,
  getSlotStatus,
  onSlotClick,
  onShowPastHours,
  onPreviousCourt,
  onNextCourt,
  currentCourtIndex = 0,
  filteredCourtsLength = 0,
  saleStep,
}) => {
  const { getGridTemplateColumns } = useCourtGridTable({
    displayedCourts,
    visibleHours,
    hasPastHours,
  });

  const isMobile = useMediaQuery("(max-width: 980px)");

  if (displayedCourts.length === 0) {
    return (
      <div className="court-grid-table-empty">
        <p>Brak dostępnych kortów dla wybranych filtrów.</p>
      </div>
    );
  }

  const handleSlotClick = (data: TimeSlotData, contextName: string) => {
    const slot = findSlot(contextName, data.startTime);
    if (slot) {
      onSlotClick(slot, contextName);
    }
  };

  const canGoPrevious = currentCourtIndex > 0;
  const canGoNext = currentCourtIndex < filteredCourtsLength - 1;

  return (
    <div className="court-grid-table">
      <div className="court-grid-table__header" style={{ gridTemplateColumns: getGridTemplateColumns() }}>
        <div className="court-grid-table__time-header">Godzina</div>
        {displayedCourts.map((court) => (
          <div key={court.courtName} className="court-grid-table__court-header">
            {isMobile && filteredCourtsLength > 1 ? (
              <div className="court-grid-table__mobile-court-nav">
                <EAIconButton
                  title="Poprzedni kort"
                  icon={<MdChevronLeft />}
                  variant="icon"
                  onClick={onPreviousCourt}
                  disabled={!canGoPrevious}
                />
                <span className="court-grid-table__court-name">{court.courtName}</span>
                <EAIconButton
                  title="Następny kort"
                  icon={<MdChevronRight />}
                  variant="icon"
                  onClick={onNextCourt}
                  disabled={!canGoNext}
                />
              </div>
            ) : (
              <GridHeader title={court.courtName} description={court.description} imageUrl={court.imageUrl} />
            )}
          </div>
        ))}
      </div>

      <div className="court-grid-table__separator"></div>

      {hasPastHours && (
        <div className="court-grid-table__past-hours-row" style={{ gridTemplateColumns: getGridTemplateColumns() }}>
          <div
            className="court-grid-table__past-hours-cell"
            style={{ gridColumn: `1 / ${displayedCourts.length + 2}` }}
          >
            <EAIconButton
              title="Pokaż wcześniejsze godziny"
              icon={<MdKeyboardArrowUp />}
              variant="icon"
              onClick={onShowPastHours}
            />
          </div>
        </div>
      )}

      <div className="court-grid-table__body">
        {visibleHours.map((hour, hourIndex) => (
          <div key={hour} className="court-grid-table__row" style={{ gridTemplateColumns: getGridTemplateColumns() }}>
            <div className="court-grid-table__time-cell">
              <p className="court-grid-table__time-text">{hour}</p>
            </div>
            {displayedCourts.map((court) => {
              const slot = findSlot(court.courtName, hour);

              return (
                <div key={`${court.courtName}-${hour}`} className="court-grid-table__slot-cell">
                  {slot ? (
                    <TimeSlot
                      data={{
                        id: slot.exerciseId,
                        startTime: slot.startTime,
                        endTime: slot.endTime,
                        capacity: slot.capacity,
                        reserved: slot.reserved,
                        status: slot.status,
                        userAssigned: slot.userAssigned,
                        saleTo: slot.internetSaleTo,
                      }}
                      contextName={court.courtName}
                      status={getSlotStatus(slot)}
                      onClick={handleSlotClick}
                      isFirstRow={hourIndex === 0}
                      showOccupancy={saleStep?.showOtherTicketsPool ?? false}
                    />
                  ) : (
                    <TimeSlot
                      data={{
                        id: 0,
                        startTime: hour,
                        endTime: hour,
                        capacity: 0,
                        reserved: 0,
                      }}
                      contextName={court.courtName}
                      status="unavailable"
                      isFirstRow={hourIndex === 0}
                      showOccupancy={saleStep?.showOtherTicketsPool ?? false}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CourtGridTable;
